/**
 * TikTok US 店铺表现（SPS）—— 静态映射与常量
 *
 * ⚠️ **i18n 约定**：这里的中文文案**本身就是 tolgee 的词条 key**（老语料库 `resources/` 的既有约定），
 * 所以本文件里**不写 t()**，而是在**渲染处**包一层：
 *   常量  nameZh: '60天差评率'      →  组件  <span>{t(meta.nameZh)}</span>
 *   常量  label:  '商品满意度'       →  组件  <Radio.Button>{t(label)}</Radio.Button>
 * 例外：`nameEn` / `STATUS_ENUM` 是**官方下发的英文原文**，属于数据不属于文案，**不做词条**。
 * 新增的中文文案要同步进 i18n 待翻译清单（`.workbuddy/i18n-TikTokUS-new-keys.md`）。
 *
 * 另外：`'跨境' / '本土'` 这类是**接口入参值**，`'导出平台数据'` 是**权限名**，都不是文案，不要 i18n。
 */

/** SPS 总分满分（环形百分比 = spsScore / 5） */
export const SPS_SCORE_MAX = 5;

/** 列表默认每页条数（与其它平台数据页保持一致） */
export const DEFAULT_PAGE_SIZE = 10;

/** 明细页时间筛选默认近 30 天（不含当天）—— 两个明细页共用这个默认值 */
export const DEFAULT_RANGE_DAYS = 30;

/** 接口失败统一文案（不区分后端 msg；与 useExportFile 内的兜底一致） */
export const FETCH_ERROR_PHRASE = 'phrase.获取数据失败，请稍后再试';

/** 「数据不足」tooltip 文案 */
export const NIL_TOOLTIP = '数据不足';

/** 详情页权益区顶部最多展示几个 chip（其余进「查看全部」弹窗） */
export const BENEFIT_CHIP_LIMIT = 4;

/** 行业务顺序固定 */
export const METRIC_CODES: Dashboard.TikTokUS.MetricCode[] = [
  'NRR',
  'NBFR',
  'SFCR',
  'OTDR',
  'AHT',
  'IM_DSAT',
];

export interface MetricMeta {
  code: Dashboard.TikTokUS.MetricCode;
  /** 官方英文名 —— **官方原文、属于数据**，所以不做 i18n 词条（指标卡第三行直接展示） */
  nameEn: string;
  /** 中文全称（PRD 口径）—— **这是词条 key**，渲染处必须包 `t()` */
  nameZh: string;
  /** 所属维度（接口下发的官方英文值） */
  dimension: Dashboard.TikTokUS.DimensionName;
  unit: Dashboard.TikTokUS.ValueUnit;
  /** 越低越好 / 越高越好 —— 决定 benchmarks 的比较方向（OTDR 是唯一「越高越好」） */
  better: 'lower' | 'higher';
  /** 是否支持问题商品下钻 /sps/metric/top-items（白名单 A） */
  supportsTopItems: boolean;
  /** 是否支持问题订单明细 /sps/problem-orders（白名单 B） */
  supportsProblemOrders: boolean;
  /** 是否支持售后处理明细 /sps/aftersales */
  supportsAftersales: boolean;
}

/**
 * ⚠️ 两组白名单**不是同一组**，各差一个指标，别互相套用：
 *   - top-items    白名单：NRR / NBFR / OTDR / AHT
 *   - 问题订单明细 白名单：NRR / NBFR / SFCR / OTDR
 * AHT / IM_DSAT 传进问题订单接口会被**明确拒绝**（400）而不是返回 0 行。
 */
export const METRIC_META: Record<Dashboard.TikTokUS.MetricCode, MetricMeta> = {
  NRR: {
    code: 'NRR',
    nameEn: '60-day negative review rate',
    nameZh: '60天差评率',
    dimension: 'Product satisfaction',
    unit: 'PERCENT',
    better: 'lower',
    supportsTopItems: true,
    supportsProblemOrders: true,
    supportsAftersales: false,
  },
  NBFR: {
    code: 'NBFR',
    nameEn: '60-day non-buyer fault r&r rate',
    nameZh: '60天非买家责任退货退款率',
    dimension: 'Product satisfaction',
    unit: 'PERCENT',
    better: 'lower',
    supportsTopItems: true,
    supportsProblemOrders: true,
    supportsAftersales: false,
  },
  SFCR: {
    code: 'SFCR',
    nameEn: '30-day seller fault cancellation rate',
    nameZh: '30天卖家责任取消率',
    dimension: 'Logistics and fulfillment',
    unit: 'PERCENT',
    better: 'lower',
    supportsTopItems: false,
    supportsProblemOrders: true,
    supportsAftersales: false,
  },
  OTDR: {
    code: 'OTDR',
    nameEn: '30-day on-time delivery rate',
    nameZh: '30天按时送达率',
    dimension: 'Logistics and fulfillment',
    unit: 'PERCENT',
    /** ⚠️ 唯一「越高越好」的指标，benchmarks 比较方向要翻转 */
    better: 'higher',
    supportsTopItems: true,
    supportsProblemOrders: true,
    supportsAftersales: false,
  },
  AHT: {
    code: 'AHT',
    nameEn: '60-day after-sales handling time',
    nameZh: '60天售后处理时长',
    dimension: 'Customer service',
    unit: 'HOURS',
    better: 'lower',
    supportsTopItems: true,
    supportsProblemOrders: false,
    supportsAftersales: true,
  },
  IM_DSAT: {
    code: 'IM_DSAT',
    nameEn: '60-day IM dissatisfaction rate',
    nameZh: '60天IM不满意率',
    dimension: 'Customer service',
    unit: 'PERCENT',
    better: 'lower',
    supportsTopItems: false,
    supportsProblemOrders: false,
    supportsAftersales: false,
  },
};

export interface DimensionMeta {
  /** 接口下发的官方英文值（dimensions[].name / metrics[].dimension） */
  value: Dashboard.TikTokUS.DimensionName;
  /** 中文文案（PRD 口径：商品满意度 / 履约物流 / 客户服务） */
  label: string;
  /** 该维度下的 2 个指标，按展示顺序 */
  metrics: Dashboard.TikTokUS.MetricCode[];
}

/** ⚠️ 运行时仍以接口下发的 dimension 字段分组为准，这里只是展示顺序与文案 */
export const DIMENSION_META: DimensionMeta[] = [
  {
    value: 'Product satisfaction',
    label: '商品满意度',
    metrics: ['NRR', 'NBFR'],
  },
  {
    value: 'Logistics and fulfillment',
    label: '履约物流',
    metrics: ['SFCR', 'OTDR'],
  },
  {
    value: 'Customer service',
    label: '客户服务',
    metrics: ['IM_DSAT', 'AHT'],
  },
];

/** 维度 Tab 上的提示文案（PRD 4.6 模板，T = 今天；恒定两行展示） */
export const RANGE_HINT = {
  sixtyDays: '商品满意度、客户服务数据统计区间',
  thirtyDays: '履约物流数据统计区间',
} as const;

/** 问题订单明细筛选器的「问题类型」选项 —— 必选、无「不限」、默认第一项（差评订单） */
export const PROBLEM_TYPE_OPTIONS: {
  value: Dashboard.TikTokUS.ProblemOrderMetricCode;
  label: string;
}[] = [
  { value: 'NRR', label: '差评订单' },
  { value: 'NBFR', label: '退货退款订单' },
  { value: 'SFCR', label: '取消订单' },
  { value: 'OTDR', label: '未按时送达订单' },
];

/** 指标状态 / 店铺分档 —— 同一套枚举，展示即用英文枚举值（不读 statusText，官方枚举、不做 i18n） */
export const STATUS_ENUM: Dashboard.TikTokUS.Status[] = [
  'EXCELLENT',
  'GOOD',
  'POOR',
  'CRITICAL',
  'NIL',
];
